/***********************/
/* Angular + external */
import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  forwardRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ICIBase } from "./interface/ic-base.interface";

import { ConstraintValue } from "shared/cubiq-services/model/core/ConstraintValue";

const LABEL: string = "STP Aggregation";

export const ALLOW_STPAGG_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICAllowStpAggComponent),
  multi: true,
};

type ComponentData = {
  disabled: boolean;
  label: string;
  value: ConstraintValue;
  locked: boolean;
  checkbox: {
    value: boolean;
  };
};

@Component({
  selector: "ic-allow-stpagg",
  templateUrl: "./html/ic-allow-stpagg.html",
  providers: [ALLOW_STPAGG_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ICAllowStpAggComponent implements OnInit, OnDestroy, ControlValueAccessor, ICIBase {
  /**********************************************************************/
  /* INPUT */

  @Input() inputId: string;

  private _input_enabledConfig: boolean = true;

  public get input_enabledConfig(): boolean {
    return this._input_enabledConfig;
  }

  @Input("config")
  public set input_enabledConfig(value: boolean) {
    if (this._input_enabledConfig !== value) {
      this.data.disabled = !value;
      this.cd.markForCheck();
    }

    this._input_enabledConfig = value;
  }

  @Input("label") input_label: any = LABEL;

  @Input("loading") isLoading: boolean = true;

  public onModelChange: Function = (value: any) => {};
  public onModelTouched: Function = () => {};

  /**********************************************************************/
  /* DATA */

  public data: ComponentData;
  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */
  constructor(public cd: ChangeDetectorRef) {
    this.initializeData();
  }

  private initializeData() {
    const context: ICAllowStpAggComponent = this;
    const data: ComponentData = {
      label: LABEL,
      value: ConstraintValue.None as ConstraintValue,
      checkbox: {
        get value() {
          return (
            data.value === ConstraintValue.FlagEnabled || data.value === ConstraintValue.Enabled
          );
        },
        set value(value) {
          if (value) {
            data.value = ConstraintValue.Enabled;
          } else {
            data.value = ConstraintValue.Disabled;
          }

          context.onModelChange(context.data.value);
        },
      },
      locked: false,
      disabled: !this._input_enabledConfig,
    } satisfies ComponentData;

    this.data = data;
  }

  /**********************************************************************/
  /* LIFE CYCLE HOOKS */
  ngOnInit() {
    this.initialize();
  }

  ngOnDestroy() {}

  /**********************************************************************/
  /* INITIALIZATION */
  public initialize(): void {
    this.data.label = this.input_label;
  }

  /**********************************************************************/
  /* ControlValueAccessor */

  public writeValue(value: ConstraintValue): void {
    if (value == null) value = ConstraintValue.None;

    this.data.value = value;
  }

  public registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  public registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* BASIC - API */

  public load(value: ConstraintValue): void {
    if (ConstraintValue.is(value)) {
      this.data.value = value;
    } else {
      logger.out.error(`ERROR: icAllowStpAgg.load(): failed to load '${value}'`);
    }
    this.cd.markForCheck();
  }

  public reset(): void {
    this.data.value = ConstraintValue.None;
    this.data.locked = false;
    this.cd.markForCheck();
  }

  public lock(): void {
    this.data.locked = true;
    this.cd.markForCheck();
  }

  public unlock(): void {
    this.data.locked = false;
    this.cd.markForCheck();
  }

  public onFocus(event: any): void {}

  public onBlur(event: any): void {}

  public set(value: any): void {}

  public validate(): boolean {
    return true;
  }
}
